import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { bomItemsApi, type BomItemSummary } from "@/lib/bom-items";

// ---------------------------------------------------------------------------
// BomItemPicker — search-input + results-list for master BOM items.
//
// Layout:
//   ┌──────────────────────────────────┐
//   │ [Search input]                   │
//   │  "Type at least 2 characters"   │  (when q.length < 2)
//   │  "No results"                    │  (when query returns [])
//   │  [Item button] Name  · CalcType  │  (results)
//   └──────────────────────────────────┘
//   [Cancel]
//
// Accessible: input uses role="searchbox" via <input type="search">.
// Result rows are real <button>s.
// ---------------------------------------------------------------------------

export interface BomItemPickerProps {
  /** Called with the chosen BomItemSummary when the user selects a result. */
  onPick: (item: BomItemSummary) => void;
  /** Called when the user clicks Cancel. Parent typically unmounts the picker. */
  onCancel: () => void;
}

export function BomItemPicker({ onPick, onCancel }: BomItemPickerProps) {
  const [q, setQ] = useState("");

  const { data: results } = useQuery({
    queryKey: ["bom-items", q],
    queryFn: () => bomItemsApi.search(q),
    enabled: q.trim().length >= 2,
  });

  const trimmed = q.trim();
  const isBelowMinLength = trimmed.length < 2;
  const hasResults = results != null && results.length > 0;
  const showEmpty = !isBelowMinLength && results != null && results.length === 0;

  return (
    <div className="flex items-center gap-2" role="region" aria-label="BOM item search">
      {/* Search input & dropdown wrapper */}
      <div className="relative w-48">
        <label htmlFor="bom-item-search" className="sr-only">
          Search BOM items
        </label>
        <Input
          id="bom-item-search"
          type="search"
          placeholder="Search BOM items…"
          value={q}
          className="h-6 text-xs"
          onChange={(e) => setQ(e.target.value)}
          autoComplete="off"
        />

        {/* Status / results Dropdown */}
        {(showEmpty || hasResults) && (
          <div className="absolute top-full left-0 mt-1 w-64 bg-background border border-border rounded-md shadow-md z-50 overflow-hidden">
            {showEmpty && (
              <p className="text-xs text-muted-foreground p-3">No results found.</p>
            )}
            
            {hasResults && (
              <ul className="max-h-64 overflow-y-auto p-1" aria-label="BOM item results">
                {results.map((item) => (
                  <li key={item.bomItemId}>
                    <button
                      type="button"
                      className="w-full rounded px-2 py-1.5 text-left text-xs hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring flex items-center justify-between"
                      onClick={() => onPick(item)}
                    >
                      <span className="font-medium truncate pr-2">{item.name}</span>
                      <span className="flex-shrink-0 flex items-center gap-1.5">
                        {item.calcType && (
                          <span className="text-[10px] text-muted-foreground uppercase bg-muted px-1 rounded-sm">
                            {item.calcType}
                          </span>
                        )}
                        {item.unitCost != null && (
                          <span className="text-[10px] text-muted-foreground">
                            ${item.unitCost.toFixed(2)} {item.unitCurrencyCode}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <Button variant="ghost" size="sm" type="button" className="h-6 px-2 text-xs text-muted-foreground hover:text-destructive" onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
}
